import { Role } from './role';
import { User } from './user';
import { UserRole } from './user-role';
import { Person } from './person';
import { Organization } from './organization';
import { ActiveStatus } from './active-status';

export class RoleEvaluator {
  constructor(
    protected user: User,
    protected roleOrCode: Role | string,
    protected statuses: ActiveStatus[] = []
  ) { }

  ofAny(usersOrIdsOrCodes: (User | number | string)[]): boolean {
    if (usersOrIdsOrCodes == null) {
      return this.user.findRoles ? this.user.findRoles(this.roleOrCode, this.statuses).length > 0 : false;
    }

    return usersOrIdsOrCodes.some(userOrIdOrCode => this.of(userOrIdOrCode));
  }

  of(targetOrIdOrCode: User | number | string): boolean {
    if (!this.roleOrCode || !targetOrIdOrCode) {
      return false;
    }

    const group = typeof targetOrIdOrCode === 'object' ? targetOrIdOrCode as User : null;
    const groupId = group  ? group.id : /^\d+$/g.test(targetOrIdOrCode.toString()) ? targetOrIdOrCode : null;
    const groupCode = group ? group.code : typeof targetOrIdOrCode === 'string' ? targetOrIdOrCode : null;

    const userRoles: UserRole[] = this.user.findRoles ? this.user.findRoles(this.roleOrCode, this.statuses) : [];

    return userRoles.some(userRole =>
      ( userRole.group.id != null && userRole.group.id === groupId ) ||
      ( userRole.group.code != null && userRole.group.code === groupCode ) ||
      ( group != null && userRole.group.isUnder != null && userRole.group.isUnder(group) )
    );
  }

  forMentee(menteeOrLoginOrId: Person | string | number): RoleEvaluator {
    return createMentorEvaluator(this.user, this.roleOrCode, this.statuses, menteeOrLoginOrId);
  }
}

class MentorEvaluator extends RoleEvaluator {
  constructor(
    user: User,
    roleOrCode: Role | string,
    statuses: ActiveStatus[] = [],
    private menteeOrLoginOrId: Person | string | number
  ) {
    super(user, roleOrCode, statuses);
  }

  of(groupOrIdOrCode: Organization | number | string): boolean {
    if (!this.roleOrCode || !this.menteeOrLoginOrId || !groupOrIdOrCode) {
      return false;
    }

    const userRoles: UserRole[] = this.user.findRoles ? this.user.findRoles(this.roleOrCode, this.statuses) : [];

    const mentee = typeof this.menteeOrLoginOrId === 'object' ? this.menteeOrLoginOrId as Person : null;
    const menteeId = mentee ?
      mentee.id :
      /^\d+$/g.test(this.menteeOrLoginOrId.toString()) ? this.menteeOrLoginOrId : null;
    const menteeLoginId = mentee ?
      mentee.loginId :
      typeof this.menteeOrLoginOrId === 'string' ? this.menteeOrLoginOrId : null;

    const group = typeof groupOrIdOrCode === 'object' ? groupOrIdOrCode as Organization : null;
    const groupId = group  ? group.id : /^\d+$/g.test(groupOrIdOrCode.toString()) ? groupOrIdOrCode : null;
    const groupCode = group ? group.code : typeof groupOrIdOrCode === 'string' ? groupOrIdOrCode : null;

    return userRoles.some(userRole =>
      userRole.role.code.endsWith('MENTOR') &&
      (
        ( userRole.group.id != null && userRole.group.id === menteeId ) ||
        ( (userRole.group as Person).loginId != null && (userRole.group as Person).loginId === menteeLoginId ) ) &&
      (
        userRole.targetMustBeInGroup == null ||
        ( userRole.targetMustBeInGroup.id != null && userRole.targetMustBeInGroup.id === groupId ) ||
        ( userRole.targetMustBeInGroup.code != null && userRole.targetMustBeInGroup.code === groupCode ) ||
        ( userRole.targetMustBeInGroup.isUnder != null && userRole.targetMustBeInGroup.isUnder(groupOrIdOrCode) )
      )
    );
  }
}

/**
 * We have a createMentorEvaluator() function to get around the tslint "variable used before declaration" error
 * @param user the mentor to evaluate
 * @param roleOrCode the role or role code to evaluate
 * @param statuses the role statuses to evaluate
 * @param menteeOrLoginOrId the mentee or mentee's login ID
 * @returns the role evaluator
 */
function createMentorEvaluator(
  user: User,
  roleOrCode: Role | string,
  statuses: ActiveStatus[],
  menteeOrLoginOrId: Person | string | number
): RoleEvaluator {
  return new MentorEvaluator(user, roleOrCode, statuses, menteeOrLoginOrId);
}



// WEBPACK FOOTER //
// ./src/gs-core/common/models/role-evaluator.ts